import { useEffect, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { ApiError, api } from '@shared/api';
import { LANGS, chatSnippet, useExampleModel, type Lang } from '../snippets';
import { ModelAccessField, sameModels, useCatalogModels } from './ModelAccess';
import { Alert, CodeBlock, CopyButton, Icon, InfoTip, Modal, Select, Tabs } from './ui';
import '../styles/billing.css';

export interface ProjectOption {
  id: string;
  name: string;
  environment: string;
}

export interface CreatedKey {
  key: string;
  name: string;
  id?: string;
  key_prefix?: string;
  created_at?: string;
  allowed_models?: string[] | null;
}

/** GET /v1/api-keys row. */
export interface ApiKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  project_id: string;
  project_name: string;
  environment: string;
  rpm_limit: number;
  tpm_limit: number;
  concurrency_limit: number;
  max_cost_per_request_idr: number | null;
  daily_budget_idr: number | null;
  monthly_budget_idr: number | null;
  expires_at: string | null;
  last_used_at: string | null;
  status: string;
  created_at: string;
  allowed_models: string[] | null;
}

const ENV_LABEL: Record<string, string> = { DEVELOPMENT: 'Dev', STAGING: 'Staging', PRODUCTION: 'Production' };

export function EnvBadge({ env }: { env: string }) {
  return <span className={env === 'PRODUCTION' ? 'tag bl-env prod' : 'tag bl-env'}>{ENV_LABEL[env] ?? env}</span>;
}

interface Limits {
  rpm: string;
  tpm: string;
  concurrency: string;
  maxCost: string;
  daily: string;
  monthly: string;
}

const str = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

function LimitsFields({
  value,
  onChange,
  budgetsLocked = false,
  open = false,
}: {
  value: Limits;
  onChange: (next: Limits) => void;
  budgetsLocked?: boolean;
  open?: boolean;
}) {
  const set = (key: keyof Limits) => (event: ChangeEvent<HTMLInputElement>) => onChange({ ...value, [key]: event.target.value });
  return (
    <details className="advanced" open={open}>
      <summary>Batas dan anggaran (opsional)</summary>
      <div className="bl-grid-3">
        <div className="field">
          <label htmlFor="rpm">Request/menit</label>
          <input id="rpm" type="number" min={1} value={value.rpm} onChange={set('rpm')} required />
        </div>
        <div className="field">
          <label htmlFor="tpm">Token/menit</label>
          <input id="tpm" type="number" min={1} value={value.tpm} onChange={set('tpm')} required />
        </div>
        <div className="field">
          <span className="label label-tip">
            <label htmlFor="conc">Paralel</label>
            <InfoTip label="Tentang paralel">Jumlah request yang boleh berjalan bersamaan. Request berikutnya ditolak sampai ada yang selesai.</InfoTip>
          </span>
          <input id="conc" type="number" min={1} value={value.concurrency} onChange={set('concurrency')} required />
        </div>
      </div>
      <div className="bl-grid-3">
        <div className="field">
          <span className="label label-tip">
            <label htmlFor="maxcost">Maks. per request</label>
            <InfoTip label="Tentang maks. per request">
              Request yang perkiraan biaya maksimumnya melebihi angka ini ditolak sebelum model dipanggil.
            </InfoTip>
          </span>
          <div className="bl-money">
            <input id="maxcost" type="number" min={0} value={value.maxCost} onChange={set('maxCost')} placeholder="Bebas" disabled={budgetsLocked} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="daily">Anggaran harian</label>
          <div className="bl-money">
            <input id="daily" type="number" min={0} value={value.daily} onChange={set('daily')} placeholder="Bebas" disabled={budgetsLocked} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="monthly">Anggaran bulanan</label>
          <div className="bl-money">
            <input id="monthly" type="number" min={0} value={value.monthly} onChange={set('monthly')} placeholder="Bebas" disabled={budgetsLocked} />
          </div>
        </div>
      </div>
      <span className="field-hint">
        {budgetsLocked
          ? 'Anggaran hanya bisa diubah oleh Owner, Admin, atau Billing organisasi.'
          : 'Kosongkan anggaran jika key cukup mengikuti batas proyek.'}
      </span>
    </details>
  );
}

const NEW_LIMITS: Limits = { rpm: '60', tpm: '150000', concurrency: '10', maxCost: '', daily: '', monthly: '' };

const money = (text: string) => (text.trim() === '' ? null : Number(text));

export function CreateKeyModal({
  projects,
  onClose,
  onCreated,
  initialProjectId,
}: {
  projects: ProjectOption[];
  onClose: () => void;
  onCreated: (result: CreatedKey) => void;
  initialProjectId?: string;
}) {
  const models = useCatalogModels();
  const [name, setName] = useState('Key utama');
  const [projectId, setProjectId] = useState(
    projects.find((p) => p.id === initialProjectId)?.id ?? projects[0]?.id ?? '',
  );
  const [allowed, setAllowed] = useState<string[] | null>(null);
  const [limits, setLimits] = useState<Limits>(NEW_LIMITS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReactNode>(null);

  const project = projects.find((p) => p.id === projectId);
  const invalidAccess = allowed !== null && allowed.length === 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (invalidAccess) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<CreatedKey>('/v1/api-keys', {
        method: 'POST',
        body: {
          project_id: projectId,
          name: name.trim(),
          rpm_limit: Number(limits.rpm),
          tpm_limit: Number(limits.tpm),
          concurrency_limit: Number(limits.concurrency),
          max_cost_per_request_idr: money(limits.maxCost) || undefined,
          daily_budget_idr: money(limits.daily) ?? undefined,
          monthly_budget_idr: money(limits.monthly) ?? undefined,
          allowed_models: allowed,
        },
      });
      onCreated({ ...result, allowed_models: allowed });
    } catch (err) {
      setError(keyError(err));
      setBusy(false);
    }
  };

  return (
    <Modal title="Buat API key" onClose={onClose} width={600}>
      {error && <Alert kind="error">{error}</Alert>}
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="kname">Nama</label>
          <input id="kname" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} autoFocus />
          <span className="field-hint">Nama aplikasi pemakainya, misalnya &quot;Website toko&quot; atau &quot;Bot WhatsApp&quot;.</span>
        </div>

        <div className="field">
          <label htmlFor="kproject">Proyek</label>
          {projects.length > 1 ? (
            <Select
              id="kproject"
              value={projectId}
              onChange={setProjectId}
              options={projects.map((p) => ({
                value: p.id,
                label: p.name,
                description: ENV_LABEL[p.environment] ?? p.environment,
                icon: <Icon name="folder" size={15} />,
              }))}
            />
          ) : (
            <div className="bl-field-static" id="kproject">
              <Icon name="folder" size={15} />
              <span className="grow truncate">{project?.name ?? '—'}</span>
              {project && <EnvBadge env={project.environment} />}
            </div>
          )}
          <span className="field-hint">Anggaran dan model cadangan proyek ikut berlaku untuk key ini.</span>
        </div>

        <ModelAccessField models={models} value={allowed} onChange={setAllowed} idPrefix="new" />

        <LimitsFields value={limits} onChange={setLimits} />

        <div className="bl-modal-foot">
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button className="btn btn-primary" disabled={busy || !projectId || !name.trim() || invalidAccess}>
            {busy ? <span className="spinner" /> : <Icon name="key" size={15} />}
            Buat key
          </button>
        </div>
      </form>
    </Modal>
  );
}

const BUDGET_ROLES = ['OWNER', 'ADMIN', 'BILLING'];

/** Org role allows budgets. */
export function useCanEditBudgets(): boolean | null {
  const [can, setCan] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ active_organization_id: string | null; memberships?: Array<{ organizationId: string; role: string }> }>('/v1/auth/me')
      .then((me) => {
        const role = me.memberships?.find((m) => m.organizationId === me.active_organization_id)?.role;
        if (alive) setCan(role ? BUDGET_ROLES.includes(role) : true);
      })
      .catch(() => alive && setCan(true));
    return () => {
      alive = false;
    };
  }, []);
  return can;
}

function keyError(err: unknown): ReactNode {
  if (!(err instanceof ApiError)) return (err as Error).message;
  if (err.status === 403) return 'Akun Anda tidak punya izin untuk perubahan ini. Minta Owner atau Admin organisasi.';
  if (/Unknown model/i.test(err.message)) {
    return `Ada model yang tidak dikenal: ${err.message.replace(/^.*Unknown model\(s\):\s*/i, '')}. Muat ulang halaman lalu pilih lagi.`;
  }
  return err.message;
}

export function EditKeyModal({
  apiKey,
  canEditBudgets,
  onClose,
  onSaved,
}: {
  apiKey: ApiKeyRow;
  canEditBudgets: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const models = useCatalogModels();
  const original: Limits = {
    rpm: str(apiKey.rpm_limit),
    tpm: str(apiKey.tpm_limit),
    concurrency: str(apiKey.concurrency_limit),
    maxCost: str(apiKey.max_cost_per_request_idr),
    daily: str(apiKey.daily_budget_idr),
    monthly: str(apiKey.monthly_budget_idr),
  };
  const [name, setName] = useState(apiKey.name);
  const [allowed, setAllowed] = useState<string[] | null>(apiKey.allowed_models);
  const [limits, setLimits] = useState<Limits>(original);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReactNode>(null);
  const [partial, setPartial] = useState<'rest' | 'none' | null>(null);

  const invalidAccess = allowed !== null && allowed.length === 0;

  const patch = () => {
    const body: Record<string, unknown> = {};
    if (name.trim() !== apiKey.name) body.name = name.trim();
    if (!sameModels(allowed, apiKey.allowed_models)) body.allowed_models = allowed;
    if (limits.rpm !== original.rpm) body.rpm_limit = Number(limits.rpm);
    if (limits.tpm !== original.tpm) body.tpm_limit = Number(limits.tpm);
    if (limits.concurrency !== original.concurrency) body.concurrency_limit = Number(limits.concurrency);
    if (limits.maxCost !== original.maxCost) body.max_cost_per_request_idr = money(limits.maxCost) || null;
    if (limits.daily !== original.daily) body.daily_budget_idr = money(limits.daily);
    if (limits.monthly !== original.monthly) body.monthly_budget_idr = money(limits.monthly);
    return body;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (invalidAccess) return;
    const body = patch();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    const budgetKeys = ['max_cost_per_request_idr', 'daily_budget_idr', 'monthly_budget_idr'];
    try {
      await api(`/v1/api-keys/${apiKey.id}`, { method: 'PATCH', body });
      onSaved();
    } catch (err) {
      const rest = Object.fromEntries(Object.entries(body).filter(([key]) => !budgetKeys.includes(key)));
      // 403: save non-budget fields
      if (err instanceof ApiError && err.status === 403 && Object.keys(rest).length < Object.keys(body).length) {
        try {
          if (Object.keys(rest).length > 0) await api(`/v1/api-keys/${apiKey.id}`, { method: 'PATCH', body: rest });
          setPartial(Object.keys(rest).length > 0 ? 'rest' : 'none');
          setLimits((current) => ({ ...current, maxCost: original.maxCost, daily: original.daily, monthly: original.monthly }));
        } catch (inner) {
          setError(keyError(inner));
        }
      } else {
        setError(keyError(err));
      }
      setBusy(false);
    }
  };

  return (
    <Modal title="Ubah API key" onClose={onClose} width={600}>
      {error && <Alert kind="error">{error}</Alert>}
      {partial && (
        <Alert kind="warn">
          {partial === 'rest' ? 'Perubahan lain sudah tersimpan, tetapi anggaran' : 'Anggaran'} tidak diubah karena butuh peran Owner,
          Admin, atau Billing organisasi.
        </Alert>
      )}
      <div className="bl-keyline">
        <span className="bl-keyline-icon">
          <Icon name="key" size={16} />
        </span>
        <div className="bl-stack">
          <span className="bl-id">
            {apiKey.key_prefix}
            <span className="bl-mask">••••</span>
          </span>
          <span className="bl-sub">
            Proyek {apiKey.project_name} · perubahan berlaku untuk request berikutnya
          </span>
        </div>
      </div>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="ename">Nama</label>
          <input id="ename" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />
        </div>

        <ModelAccessField models={models} value={allowed} onChange={setAllowed} idPrefix="edit" />

        <LimitsFields value={limits} onChange={setLimits} budgetsLocked={!canEditBudgets} />

        <div className="bl-modal-foot">
          <button type="button" className="btn" onClick={partial ? onSaved : onClose}>
            {partial ? 'Tutup' : 'Batal'}
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim() || invalidAccess}>
            {busy ? <span className="spinner" /> : <Icon name="check" size={15} />}
            Simpan perubahan
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Key shown once. */
export function KeyRevealModal({ created, onDone }: { created: CreatedKey; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [lang, setLang] = useState<Lang>('curl');
  const fallback = useExampleModel();
  const catalog = useCatalogModels();
  const allowed = created.allowed_models ?? null;
  // example must be allowed
  const model = allowed
    ? (allowed.find((slug) => (catalog?.find((m) => m.id === slug)?.modality ?? 'text') === 'text') ?? allowed[0])
    : fallback;
  const names = allowed?.map((slug) => catalog?.find((m) => m.id === slug)?.display_name ?? slug);

  return (
    <Modal title="Simpan API key Anda" onClose={onDone} width={660} dismissible={false}>
      <div className="field">
        <span className="label">API key</span>
        <div className="bl-reveal-row">
          <div className="key-reveal" data-testid="key-reveal">
            {created.key}
          </div>
          <CopyButton value={created.key} label="Salin key" />
        </div>
        <span className="field-hint">
          {names ? <>Hanya bisa memanggil {names.join(', ')}.</> : 'Bisa memanggil semua model.'} Akses model bisa diubah kapan saja lewat tombol Ubah.
        </span>
      </div>

      <div className="field">
        <div className="row between">
          <span className="label">Coba sekarang</span>
          <span className="field-hint">
            Sudah berisi key ini, model <span className="mono">{model}</span>
          </span>
        </div>
        <CodeBlock
          code={chatSnippet(lang, { apiKey: created.key, model })}
          tabs={<Tabs tight tabs={LANGS} value={lang} onChange={setLang} />}
        />
      </div>

      <label className={saved ? 'bl-confirm on' : 'bl-confirm'}>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        Saya sudah menyimpan key ini
      </label>

      <div className="bl-modal-foot">
        {/* new tab keeps key */}
        <a className="btn btn-ghost" href="/docs" target="_blank" rel="noreferrer">
          <Icon name="book" size={15} />
          Dokumentasi
          <Icon name="external" size={13} />
        </a>
        <span className="grow" />
        <button className="btn btn-primary" disabled={!saved} onClick={onDone}>
          <Icon name="check" size={15} />
          Selesai
        </button>
      </div>
    </Modal>
  );
}
